import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toProperCase } from '../../utils/textFormat';
import { Building, Users, FileText, Building2, ChevronRight, X, Plus, UserPlus, CreditCard, ArrowRight, AlertTriangle, Check, Search, Trash2, Eye, EyeOff, Database, Loader2, BarChart3, RefreshCw, Flag } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

import { API_BASE as API } from '../../config';

const ROLE_COLORS = {
  super_admin: 'text-red-400 bg-red-400/10',
  admin: 'text-purple-400 bg-purple-400/10',
  manager: 'text-blue-400 bg-blue-400/10',
  member: 'text-emerald-400 bg-emerald-400/10',
  viewer: 'text-slate-400 bg-slate-700',
};

const PLANS = {
  complimentary: {
    label: 'Complimentary', color: 'text-emerald-400 bg-emerald-400/10', badgeBorder: 'border-emerald-500 bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    monthly: 0, yearly: 0, admins: 1, users: 2, companies: 1, orgs: 0, claims: 50,
    features: ['1 admin, 2 users', '1 company', '50 claims/month', 'Never expires'],
    desc: 'Free, no expiry',
  },
  free_trial: {
    label: 'Free Trial', color: 'text-blue-400 bg-blue-400/10', badgeBorder: 'border-blue-500 bg-blue-500/15 text-blue-300 ring-blue-500/30',
    monthly: 0, yearly: 0, admins: 1, users: 2, companies: 3, orgs: 0, claims: 100,
    features: ['1 admin, 2 users', '3 companies', '100 claims/month', 'Full access for 30 days'],
    desc: '$0 for 30 days',
  },
  solo: {
    label: 'Solo', color: 'text-amber-400 bg-amber-400/10', badgeBorder: 'border-amber-500 bg-amber-500/15 text-amber-300 ring-amber-500/30',
    monthly: 39, yearly: 390, admins: 1, users: 2, companies: 1, orgs: 0, claims: Infinity,
    features: ['1 admin, 2 users (base)', '1 company (base)', 'Unlimited claims', '+$29/company, +$11/admin, +$9/user'],
    desc: '$39/mo',
  },
  provider: {
    label: 'Provider', color: 'text-purple-400 bg-purple-400/10', badgeBorder: 'border-purple-500 bg-purple-500/15 text-purple-300 ring-purple-500/30',
    monthly: 99, yearly: 990, admins: 1, users: 2, companies: 6, orgs: 3, claims: Infinity,
    features: ['1 admin, 2 users (base)', '3 organizations, 6 companies', 'Unlimited claims', '+$30/org, +$9/company, +$7/admin, +$5/user'],
    desc: '$99/mo',
  },
  enterprise: {
    label: 'Enterprise', color: 'text-rose-400 bg-rose-400/10', badgeBorder: 'border-rose-500 bg-rose-500/15 text-rose-300 ring-rose-500/30',
    monthly: 349, yearly: 3490, admins: 3, users: Infinity, companies: Infinity, orgs: 5, claims: Infinity,
    features: ['3 admins, unlimited users', '5 organizations, unlimited companies', 'Cross-org reporting, API access', '+$35/org, +$20/viewer'],
    desc: '$349/mo',
  },
};
const PLAN_KEYS = Object.keys(PLANS);
const PLAN_ORDER = { complimentary: 0, free_trial: 1, solo: 2, provider: 3, enterprise: 4 };

const getPlanBadge = (plan) => {
  const p = PLANS[plan];
  if (!p) return <span className="text-[10px] px-1.5 py-0.5 rounded text-slate-600 bg-slate-800">Not Set</span>;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${p.color}`}>{p.label}</span>;
};

function buildPlanDiff(fromKey, toKey, detail) {
  const from = PLANS[fromKey] || PLANS.solo;
  const to = PLANS[toKey] || PLANS.solo;
  const isUpgrade = PLAN_ORDER[toKey] > PLAN_ORDER[fromKey];
  const monthDiff = to.monthly - from.monthly;
  const yearDiff = to.yearly - from.yearly;

  const sub = detail?.subscription;
  const subStatus = sub?.status || 'none';
  const hasStripe = !!sub?.stripe_subscription_id;

  const changes = [];
  const warnings = [];

  // Limit comparisons
  const limitFields = [
    { key: 'admins', label: 'Admins', current: detail?.admin_count || 0 },
    { key: 'users', label: 'Users', current: detail?.member_count || 0 },
    { key: 'companies', label: 'Companies', current: detail?.company_count || 0 },
    { key: 'orgs', label: 'Organizations', current: detail?.org_count || 0 },
    { key: 'claims', label: 'Claims/month', current: 0 },
  ];

  for (const f of limitFields) {
    const oldVal = from[f.key] || 0;
    const newVal = to[f.key] || 0;
    if (oldVal === newVal) {
      if (newVal > 0 || newVal === Infinity) changes.push({ text: `${f.label}: ${newVal === Infinity ? 'Unlimited' : newVal}`, type: 'unchanged' });
    } else {
      const oldLabel = oldVal === Infinity ? 'Unlimited' : oldVal;
      const newLabel = newVal === Infinity ? 'Unlimited' : newVal;
      changes.push({ text: `${f.label}: ${oldLabel} → ${newLabel}`, type: newVal > oldVal || newVal === Infinity ? 'added' : 'removed' });
      // Over-limit warning
      if (newVal !== Infinity && f.current > newVal) {
        warnings.push(`Currently ${f.current} ${f.label.toLowerCase()} (${f.current - newVal} over new limit). Existing won't be removed but new can't be added.`);
      }
    }
  }

  // Stripe warning
  if (hasStripe && subStatus === 'active') {
    warnings.push('This tenant has an active Stripe subscription. The subscription will be modified with prorated charges.');
  }

  return { isUpgrade, monthDiff, yearDiff, changes, warnings, from, to, subStatus, hasStripe };
}

function DevTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDemo, setShowDemo] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [confirmDeleteTenant, setConfirmDeleteTenant] = useState(null);
  const [showAddTenant, setShowAddTenant] = useState(false);
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantPlan, setNewTenantPlan] = useState('free_trial');
  const [addingTenant, setAddingTenant] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: '', dot_number: '', contact: '', email: '', phone: '' });
  const [addingCompany, setAddingCompany] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', password: '', role: 'member' });
  const [addingMember, setAddingMember] = useState(false);
  const [showAddExisting, setShowAddExisting] = useState(false);
  const [existingSearch, setExistingSearch] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [selectedExisting, setSelectedExisting] = useState(null);
  const [existingRole, setExistingRole] = useState('member');
  const [addingExisting, setAddingExisting] = useState(false);
  const [planConfirm, setPlanConfirm] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [migrationProgress, setMigrationProgress] = useState(null);
  const migrationAbortRef = useRef(false);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [uploadsDiag, setUploadsDiag] = useState(null);
  const [gridfsMigrating, setGridfsMigrating] = useState(false);
  const [orphanData, setOrphanData] = useState(null);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [orphanAssigning, setOrphanAssigning] = useState(false);
  const [billingAudit, setBillingAudit] = useState(null);
  const [billingAuditLoading, setBillingAuditLoading] = useState(false);
  const [billingSyncing, setBillingSyncing] = useState(false);
  const [featureFlags, setFeatureFlags] = useState(null);
  const [featureFlagSaving, setFeatureFlagSaving] = useState(null); // key currently saving

  useEffect(() => { fetchTenants(); }, [showDemo]);

  const fetchTenants = async () => {
    try {
      const res = await axios.get(`${API}/dev/tenants`, { params: { include_demo: showDemo }, withCredentials: true });
      setTenants(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchBillingAudit = async (tenantId) => {
    setBillingAuditLoading(true);
    try {
      const res = await axios.get(`${API}/dev/tenants/${tenantId}/billing-audit`, { withCredentials: true });
      setBillingAudit(res.data);
    } catch (err) { setBillingAudit({ error: err.response?.data?.detail || 'Failed' }); }
    finally { setBillingAuditLoading(false); }
  };

  const syncTenantBilling = async (tenantId, prorate) => {
    setBillingSyncing(true);
    try {
      const res = await axios.post(
        `${API}/dev/tenants/${tenantId}/sync-billing`,
        { proration_behavior: prorate ? 'create_prorations' : 'none' },
        { withCredentials: true }
      );
      setBillingAudit(res.data.audit);
      const sync = res.data.sync || {};
      if (sync.synced) {
        toast.success(`Synced ${sync.scope === 'local_only' ? 'locally' : 'to Stripe'}: $${(sync.old_monthly || 0).toFixed(2)} → $${(sync.new_monthly || 0).toFixed(2)}`);
      } else {
        toast.info(sync.reason || 'No change');
      }
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setBillingSyncing(false);
    }
  };

  const fetchDetail = async (tenantId) => {
    setSelected(tenantId);
    setShowAddCompany(false);
    setShowAddMember(false);
    setMigrationStatus(null);
    setMigrationResult(null);
    setOrphanData(null);
    setBillingAudit(null);
    setFeatureFlags(null);
    try {
      const res = await axios.get(`${API}/dev/tenants/${tenantId}`, { withCredentials: true });
      setDetail(res.data);
      // Also auto-load billing audit + feature flags
      fetchBillingAudit(tenantId);
      fetchFeatureFlags(tenantId);
    } catch (err) { console.error(err); }
  };

  const fetchFeatureFlags = async (tenantId) => {
    try {
      const res = await axios.get(`${API}/dev/tenants/${tenantId}/features`, { withCredentials: true });
      setFeatureFlags(res.data);
    } catch (err) {
      // 404 fine if tenant just got deleted; surface real errors
      if (err.response?.status !== 404) console.error('Failed to load feature flags:', err?.message);
    }
  };

  const toggleFeatureFlag = async (key, enabled) => {
    if (!detail?.tenant_id) return;
    setFeatureFlagSaving(key);
    try {
      const res = await axios.put(
        `${API}/dev/tenants/${detail.tenant_id}/features`,
        { key, enabled },
        { withCredentials: true },
      );
      setFeatureFlags(res.data);
      toast.success(`${enabled ? 'Enabled' : 'Disabled'} ${key.replaceAll('_', ' ')}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update feature flag');
    } finally {
      setFeatureFlagSaving(null);
    }
  };

  const handleAddTenant = async (e) => {
    e.preventDefault();
    setAddingTenant(true);
    try {
      await axios.post(`${API}/dev/tenants`, { name: newTenantName, billing_plan: newTenantPlan }, { withCredentials: true });
      toast.success(`Tenant "${newTenantName}" created`);
      setNewTenantName('');
      setNewTenantPlan('free_trial');
      setShowAddTenant(false);
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create tenant');
    } finally { setAddingTenant(false); }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    setAddingCompany(true);
    try {
      await axios.post(`${API}/dev/companies`, { ...newCompany, tenant_id: selected }, { withCredentials: true });
      toast.success(`Company "${newCompany.name}" created`);
      setNewCompany({ name: '', dot_number: '', contact: '', email: '', phone: '' });
      setShowAddCompany(false);
      fetchDetail(selected);
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create company');
    } finally { setAddingCompany(false); }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setAddingMember(true);
    try {
      await axios.post(`${API}/dev/users`, { ...newMember, tenant_id: selected }, { withCredentials: true });
      toast.success(`Member "${newMember.name}" added`);
      setNewMember({ name: '', email: '', password: '', role: 'member' });
      setShowAddMember(false);
      fetchDetail(selected);
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    } finally { setAddingMember(false); }
  };

  const searchAvailableUsers = async (query) => {
    setExistingSearch(query);
    if (!selected) return;
    setLoadingAvailable(true);
    try {
      const res = await axios.get(`${API}/dev/users/available`, { params: { tenant_id: selected, search: query }, withCredentials: true });
      setAvailableUsers(res.data);
    } catch { setAvailableUsers([]); }
    finally { setLoadingAvailable(false); }
  };

  const handleOpenAddExisting = () => {
    setShowAddExisting(true);
    setShowAddMember(false);
    setSelectedExisting(null);
    setExistingRole('member');
    setExistingSearch('');
    searchAvailableUsers('');
  };

  const handleAddExistingMember = async () => {
    if (!selectedExisting) return;
    setAddingExisting(true);
    try {
      await axios.post(`${API}/dev/tenants/${selected}/add-member`, { user_id: selectedExisting.user_id, role: existingRole }, { withCredentials: true });
      toast.success(`${selectedExisting.name} added to organization`);
      setShowAddExisting(false);
      setSelectedExisting(null);
      fetchDetail(selected);
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add user');
    } finally { setAddingExisting(false); }
  };

  const handlePlanChange = async (plan) => {
    if (!selected || !detail || detail.billing_plan === plan) return;
    const diff = buildPlanDiff(detail.billing_plan, plan, detail);
    let costPreview = null;
    if (['solo', 'provider', 'enterprise'].includes(plan)) {
      try {
        const res = await axios.get(`${API}/dev/tenants/${selected}/plan-preview/${plan}`, { withCredentials: true });
        costPreview = res.data;
      } catch (err) { console.warn('[DevTenants] Failed to load plan preview', err); }
    }
    setPlanConfirm({ plan, diff, tenantName: detail.name, costPreview });
  };

  const confirmPlanChange = async () => {
    if (!planConfirm || !selected) return;
    const plan = planConfirm.plan;
    setPlanConfirm(null);
    try {
      const res = await axios.put(`${API}/dev/tenants/${selected}/billing-plan`, { billing_plan: plan }, { withCredentials: true });
      const stripeNote = res.data.stripe_modified ? ' (Stripe updated)' : '';
      toast.success(`Plan updated to ${PLANS[plan]?.label}${stripeNote}`);
      // Refresh detail to get updated subscription info
      fetchDetail(selected);
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update plan');
    }
  };

  const handleDeleteTenant = async () => {
    const t = confirmDeleteTenant;
    if (!t) return;
    setConfirmDeleteTenant(null);
    try {
      await axios.delete(`${API}/dev/tenants/${t.tenant_id}`, { withCredentials: true });
      toast.success(`${t.name} deleted`);
      if (selected === t.tenant_id) { setSelected(null); setDetail(null); }
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete tenant');
    }
  };

  const handleMigration = async (migratePictures, migrateNotes) => {
    if (!selected) return;
    migrationAbortRef.current = false;
    setMigrating(true);
    setMigrationResult(null);
    setMigrationProgress(null);

    const totals = {
      batches: 0,
      pictures: { files_downloaded: 0, claims_updated: 0, failed: 0, total_checked: 0, json_strings_parsed: 0, error_samples: [] },
      notes: { inserted: 0, duplicates_skipped: 0 },
    };

    let hasMore = true;
    const BATCH = 5;
    let notesProcessed = false;

    while (hasMore && !migrationAbortRef.current) {
      try {
        // Notes only need to run once (they process all claims), so skip on subsequent batches
        const sendNotes = migrateNotes && !notesProcessed;
        const res = await axios.post(`${API}/dev/migrate-data`, {
          tenant_id: selected,
          migrate_pictures: migratePictures,
          migrate_notes: sendNotes,
          batch_size: BATCH,
        }, { withCredentials: true, timeout: 120000 });

        const d = res.data;
        totals.batches += 1;

        if (d.pictures) {
          totals.pictures.files_downloaded += d.pictures.files_downloaded || 0;
          totals.pictures.claims_updated += d.pictures.claims_updated || 0;
          totals.pictures.failed += d.pictures.failed || 0;
          totals.pictures.total_checked += d.pictures.total_checked || 0;
          totals.pictures.json_strings_parsed += d.pictures.json_strings_parsed || 0;
          if (d.pictures.error_samples && totals.pictures.error_samples.length < 5) {
            totals.pictures.error_samples.push(...d.pictures.error_samples);
          }
        }
        if (d.notes) {
          totals.notes.inserted += d.notes.inserted || 0;
          totals.notes.duplicates_skipped += d.notes.duplicates_skipped || 0;
          notesProcessed = true;
        }

        setMigrationProgress({ ...totals });

        // Use backend's completed flag — if no claims were found needing work, we're done
        const picsDone = !d.pictures || d.pictures.completed || d.pictures.total_checked === 0;
        const notesDone = !d.notes || d.notes.inserted === 0;
        if (picsDone && notesDone) {
          hasMore = false;
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || `Batch ${totals.batches + 1} failed — ${err.message}`);
        hasMore = false;
      }
    }

    const final = {
      pictures: migratePictures ? totals.pictures : null,
      notes: migrateNotes ? totals.notes : null,
      batches: totals.batches,
      stopped: migrationAbortRef.current,
    };
    setMigrationResult(final);
    setMigrationProgress(null);
    setMigrating(false);

    if (migrationAbortRef.current) {
      toast.info('Migration stopped by user');
    } else if (totals.pictures.files_downloaded > 0 || totals.notes.inserted > 0) {
      toast.success(`Migration complete — ${totals.pictures.files_downloaded} files, ${totals.notes.inserted} notes in ${totals.batches} batches`);
    } else {
      toast.success('Migration complete — nothing remaining to migrate');
    }
  };

  const stopMigration = () => { migrationAbortRef.current = true; };

  const fetchMigrationStatus = async () => {
    if (!selected) return;
    setStatusLoading(true);
    try {
      const { data } = await axios.get(`${API}/dev/migration-status/${selected}`, { withCredentials: true });
      setMigrationStatus(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to get status'); }
    finally { setStatusLoading(false); }
  };

  const retryFailedMigration = async () => {
    if (!selected) return;
    try {
      const { data } = await axios.post(`${API}/dev/migration-retry-failed/${selected}`, {}, { withCredentials: true });
      toast.success(data.message);
      setMigrationStatus(null);
      fetchMigrationStatus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to clear flags'); }
  };

  const fetchUploadsDiag = async () => {
    try {
      const { data } = await axios.get(`${API}/dev/uploads-diagnostic`, { withCredentials: true });
      setUploadsDiag(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to get diagnostic'); }
  };

  const migrateToGridFS = async () => {
    setGridfsMigrating(true);
    let totalMigrated = 0;
    let completed = false;
    try {
      while (!completed) {
        const { data } = await axios.post(`${API}/dev/migrate-to-gridfs?batch_size=100`, {}, { withCredentials: true });
        totalMigrated += data.migrated;
        completed = data.completed;
        if (data.migrated > 0) {
          toast.success(`Migrated ${totalMigrated} files to GridFS... ${data.remaining} remaining`);
        }
      }
      toast.success(`GridFS migration complete! ${totalMigrated} files migrated.`);
      fetchUploadsDiag();
    } catch (err) { toast.error(err.response?.data?.detail || 'GridFS migration failed'); }
    finally { setGridfsMigrating(false); }
  };

  const fetchOrphans = async () => {
    setOrphanLoading(true);
    try {
      const { data } = await axios.get(`${API}/dev/orphan-claims`, { withCredentials: true });
      setOrphanData(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to fetch orphans'); }
    finally { setOrphanLoading(false); }
  };

  const assignOrphans = async (companyName) => {
    if (!selected) return;
    setOrphanAssigning(true);
    try {
      const payload = { tenant_id: selected };
      if (companyName) payload.company_name = companyName;
      const { data } = await axios.post(`${API}/dev/orphan-claims/assign`, payload, { withCredentials: true });
      toast.success(`Assigned ${data.updated} claims to ${data.tenant_name}`);
      fetchOrphans();
    } catch (err) { toast.error(err.response?.data?.detail || 'Assign failed'); }
    finally { setOrphanAssigning(false); }
  };

  const filtered = tenants.filter((t) => {
    if (search && !t.name.toLowerCase().includes(search.toLowerCase()) && !t.tenant_id.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterPlan !== 'all' && t.billing_plan !== filterPlan) return false;
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400" /></div>;
  }

  return (
    <div className="p-8" data-testid="dev-tenants">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Tenants & Organizations</h1>
          <p className="text-sm text-slate-400 mt-1">{tenants.length} tenant{tenants.length !== 1 ? 's' : ''} registered{!showDemo ? ' (demo hidden)' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowDemo(!showDemo)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${showDemo ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600'}`}
            data-testid="toggle-demo-tenants"
            title={showDemo ? 'Hide demo tenants' : 'Show demo tenants'}
          >
            {showDemo ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {showDemo ? 'Demo Visible' : 'Demo Hidden'}
          </button>
          <Button onClick={() => setShowAddTenant(!showAddTenant)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-sm" data-testid="add-tenant-btn">
            <Plus className="w-4 h-4 mr-1.5" />
            New Tenant
          </Button>
        </div>
      </div>

      {/* Add Tenant Form */}
      {showAddTenant && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 mb-6" data-testid="add-tenant-form">
          <h3 className="text-sm font-semibold text-white mb-3">Create Tenant</h3>
          <form onSubmit={handleAddTenant} className="space-y-4">
            <div className="flex gap-3 items-end">
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-slate-400">Organization Name</Label>
                <Input value={newTenantName} onChange={(e) => setNewTenantName(toProperCase(e.target.value))} placeholder="e.g. Acme Corp" className="bg-slate-800 border-slate-700 text-slate-200 h-9" required data-testid="new-tenant-name" />
              </div>
              <div className="w-44 space-y-1">
                <Label className="text-xs text-slate-400">Billing Plan</Label>
                <Select value={newTenantPlan} onValueChange={setNewTenantPlan}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-300 h-9 text-sm" data-testid="new-tenant-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAN_KEYS.map((k) => (
                      <SelectItem key={k} value={k}>
                        <span className="flex items-center gap-2">{PLANS[k].label} <span className="text-[10px] text-slate-500">{PLANS[k].desc}</span></span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Plan summary card */}
            {PLANS[newTenantPlan] && (
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-white">{PLANS[newTenantPlan].label} Plan</span>
                  <span className="text-sm font-semibold text-white">
                    {PLANS[newTenantPlan].monthly === 0 ? (newTenantPlan === 'free_trial' ? '$0 for 30 days' : '$0/mo') : `$${PLANS[newTenantPlan].monthly}/mo`}
                    {PLANS[newTenantPlan].yearly > 0 && <span className="text-[10px] text-slate-500 ml-1">(${PLANS[newTenantPlan].yearly}/yr)</span>}
                  </span>
                </div>
                <ul className="space-y-1">
                  {PLANS[newTenantPlan].features.map((f, i) => (
                    <li key={f || i} className="text-xs text-slate-400 flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500 shrink-0" />{f}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" onClick={() => setShowAddTenant(false)} className="text-slate-400 h-9 text-sm">Cancel</Button>
              <Button type="submit" disabled={addingTenant} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-sm" data-testid="create-tenant-submit">
                {addingTenant ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="flex gap-6">
        {/* Tenant List */}
        <div className={`${selected ? 'w-1/2' : 'w-full'} transition-all`}>
          {/* Search & Filters */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tenants..." className="bg-slate-900 border-slate-800 text-slate-200 h-9 pl-9 text-sm" data-testid="tenant-search" />
            </div>
            <Select value={filterPlan} onValueChange={setFilterPlan}>
              <SelectTrigger className="bg-slate-900 border-slate-800 text-slate-300 h-9 w-40 text-sm" data-testid="tenant-filter-plan">
                <SelectValue placeholder="All Plans" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                {PLAN_KEYS.map((k) => <SelectItem key={k} value={k}>{PLANS[k].label}</SelectItem>)}
              </SelectContent>
            </Select>
            {(search || filterPlan !== 'all') && (
              <span className="text-xs text-slate-500">{filtered.length} of {tenants.length}</span>
            )}
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-5 py-3">Organization</th>
                  <th className="text-center text-xs text-slate-500 uppercase tracking-wider px-3 py-3">Plan</th>
                  <th className="text-center text-xs text-slate-500 uppercase tracking-wider px-3 py-3">Users</th>
                  <th className="text-center text-xs text-slate-500 uppercase tracking-wider px-3 py-3">Claims</th>
                  <th className="text-center text-xs text-slate-500 uppercase tracking-wider px-3 py-3">Companies</th>
                  <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-3 py-3">Owner / Billing Email</th>
                  <th className="text-right text-xs text-slate-500 uppercase tracking-wider px-3 py-3">Created</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filtered.map((t) => (
                  <tr key={t.tenant_id} onClick={() => fetchDetail(t.tenant_id)} className={`cursor-pointer transition-colors ${selected === t.tenant_id ? 'bg-slate-800/60' : 'hover:bg-slate-800/40'}`} data-testid={`tenant-row-${t.tenant_id}`}>
                    <td className="px-5 py-3">
                      <p className="text-sm text-slate-200 font-medium">{t.name}</p>
                      <p className="text-[10px] text-slate-600 font-mono">{t.tenant_id}</p>
                    </td>
                    <td className="px-3 py-3 text-center">{getPlanBadge(t.billing_plan)}</td>
                    <td className="px-3 py-3 text-center"><span className="text-sm text-blue-400">{t.user_count}</span></td>
                    <td className="px-3 py-3 text-center"><span className="text-sm text-amber-400">{t.claim_count}</span></td>
                    <td className="px-3 py-3 text-center"><span className="text-sm text-purple-400">{t.company_count}</span></td>
                    <td className="px-3 py-3 text-xs">
                      {t.owner_email ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-slate-300 truncate max-w-[220px]" title={t.owner_email}>{t.owner_email}</span>
                          {t.stripe_customer_id && (
                            t.billing_email_mismatch ? (
                              <span className="text-[10px] text-amber-400 truncate max-w-[220px]" title={`Stripe: ${t.stripe_customer_email}`}>
                                ⚠ Stripe: {t.stripe_customer_email || '—'}
                              </span>
                            ) : (
                              <span className="text-[10px] text-emerald-500/70">Stripe email matches</span>
                            )
                          )}
                          {!t.stripe_customer_id && (
                            <span className="text-[10px] text-slate-600">No Stripe customer</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right text-xs text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}</td>
                    <td className="pr-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteTenant(t); }}
                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-red-500/15 text-slate-700 hover:text-red-400 transition-colors mr-1"
                        data-testid={`tenant-delete-${t.tenant_id}`}
                        title={`Delete ${t.name}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <ChevronRight className="w-4 h-4 text-slate-600 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <p className="px-5 py-8 text-sm text-slate-500 text-center">{tenants.length === 0 ? 'No tenants found' : 'No tenants match filters'}</p>}
          </div>
        </div>

        {/* Detail Panel */}
        {selected && detail && (
          <div className="w-1/2 bg-slate-900 border border-slate-800 rounded-lg overflow-hidden max-h-[calc(100vh-200px)] overflow-y-auto" data-testid="tenant-detail-panel">
            <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between sticky top-0 bg-slate-900 z-10">
              <div>
                <h2 className="text-sm font-semibold text-white">{detail.name}</h2>
                <p className="text-[10px] text-slate-500 font-mono">{detail.tenant_id}</p>
              </div>
              <button onClick={() => { setSelected(null); setDetail(null); }} className="text-slate-500 hover:text-slate-300"><X className="w-4 h-4" /></button>
            </div>

            <div className="px-5 py-4 border-b border-slate-800 grid grid-cols-3 gap-4">
              <div className="text-center"><Users className="w-4 h-4 text-blue-400 mx-auto mb-1" /><p className="text-lg font-bold text-white">{detail.user_count}</p><p className="text-[10px] text-slate-500">Users</p></div>
              <div className="text-center"><FileText className="w-4 h-4 text-amber-400 mx-auto mb-1" /><p className="text-lg font-bold text-white">{detail.claim_count}</p><p className="text-[10px] text-slate-500">Claims</p></div>
              <div className="text-center"><Building2 className="w-4 h-4 text-purple-400 mx-auto mb-1" /><p className="text-lg font-bold text-white">{detail.company_count}</p><p className="text-[10px] text-slate-500">Companies</p></div>
            </div>

            {/* Billing Plan */}
            <div className="px-5 py-3 border-b border-slate-800">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><CreditCard className="w-3 h-3" /> Billing Plan</h3>
                {PLANS[detail.billing_plan] && (
                  <span className="text-xs text-white font-semibold">
                    {PLANS[detail.billing_plan].monthly === 0 ? '$0/mo' : `$${PLANS[detail.billing_plan].monthly}/mo`}
                  </span>
                )}
              </div>
              {/* Subscription Status */}
              {detail.subscription && (
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      detail.subscription.status === 'active' ? 'text-emerald-400 bg-emerald-400/10' :
                      detail.subscription.status === 'trialing' ? 'text-blue-400 bg-blue-400/10' :
                      detail.subscription.status === 'trial_expired' ? 'text-red-400 bg-red-400/10' :
                      'text-slate-400 bg-slate-700'
                    }`}>{(detail.subscription.status || 'none').replace('_', ' ')}</span>
                    {detail.subscription.stripe_subscription_id && (
                      <span className="text-[10px] text-slate-600 font-mono">Stripe</span>
                    )}
                    {detail.subscription.billing?.total_monthly > 0 && (
                      <span className="text-[10px] text-slate-400">${detail.subscription.billing.total_monthly}/mo</span>
                    )}
                  </div>
                  {detail.subscription.status === 'trialing' && detail.subscription.trial_end && (
                    <p className="text-[10px] text-slate-500">Trial ends {new Date(detail.subscription.trial_end).toLocaleDateString()}</p>
                  )}
                </div>
              )}
              {/* Usage bars from real plan limits */}
              {PLANS[detail.billing_plan] && (
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {[
                    { label: 'Admins', current: detail.admin_count || 0, limit: PLANS[detail.billing_plan].admins },
                    { label: 'Users', current: detail.member_count || 0, limit: PLANS[detail.billing_plan].users },
                    { label: 'Companies', current: detail.company_count || 0, limit: PLANS[detail.billing_plan].companies },
                    ...(PLANS[detail.billing_plan].orgs > 0 ? [{ label: 'Orgs', current: detail.org_count || 0, limit: PLANS[detail.billing_plan].orgs }] : []),
                  ].map((bar) => (
                    <div key={bar.label}>
                      <div className="flex justify-between text-[10px] mb-0.5">
                        <span className="text-slate-500">{bar.label}</span>
                        <span className="text-slate-400">{bar.current} / {bar.limit === Infinity ? '∞' : bar.limit}</span>
                      </div>
                      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${bar.limit !== Infinity && bar.current > bar.limit ? 'bg-red-500' : 'bg-blue-500'}`}
                          style={{ width: `${bar.limit === Infinity ? 5 : Math.min(100, (bar.current / bar.limit) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-1.5 flex-wrap">
                {PLAN_KEYS.map((k) => (
                  <button
                    key={k}
                    onClick={() => handlePlanChange(k)}
                    data-testid={`plan-btn-${k}`}
                    className={`text-[11px] px-2.5 py-1 rounded-md border transition-all ${detail.billing_plan === k
                      ? `${PLANS[k].badgeBorder} ring-1 font-medium`
                      : 'border-slate-700 bg-slate-800/60 text-slate-500 hover:border-slate-600 hover:text-slate-400'
                    }`}
                  >
                    {PLANS[k].label}
                  </button>
                ))}
              </div>
              {detail.billing_plan_set_at && (
                <p className="text-[10px] text-slate-600 mt-1.5">Set {new Date(detail.billing_plan_set_at).toLocaleDateString()}</p>
              )}
            </div>

            {/* Feature Flags — dev-only per-tenant toggles */}
            <div className="px-5 py-3 border-b border-slate-800" data-testid="feature-flags-panel">
              <h3 className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Flag className="w-3 h-3" /> Feature Flags
              </h3>
              {!featureFlags ? (
                <p className="text-[10px] text-slate-600">Loading…</p>
              ) : featureFlags.flags.length === 0 ? (
                <p className="text-[10px] text-slate-600">No dev-toggleable flags registered.</p>
              ) : (
                <div className="space-y-1.5">
                  {featureFlags.flags.map((f) => {
                    const saving = featureFlagSaving === f.key;
                    const muted = !f.applicable;
                    return (
                      <div
                        key={f.key}
                        className={`flex items-start gap-2 px-2.5 py-2 rounded border ${
                          muted ? 'border-slate-800 bg-slate-900/40 opacity-50' : 'border-slate-800 bg-slate-900/60'
                        }`}
                        data-testid={`feature-flag-row-${f.key}`}
                      >
                        <button
                          type="button"
                          disabled={muted || saving}
                          onClick={() => toggleFeatureFlag(f.key, !f.enabled)}
                          className={`mt-0.5 relative w-9 h-5 rounded-full transition-colors shrink-0 ${
                            f.enabled ? 'bg-emerald-500' : 'bg-slate-700'
                          } ${muted ? 'cursor-not-allowed' : ''}`}
                          data-testid={`feature-flag-toggle-${f.key}`}
                          aria-label={`Toggle ${f.label}`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                              f.enabled ? 'translate-x-4' : ''
                            }`}
                          />
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] font-medium text-slate-200">{f.label}</span>
                            {f.provider_only && (
                              <span className="text-[9px] uppercase font-semibold px-1 py-0.5 rounded bg-blue-900/40 text-blue-300 border border-blue-700/40">
                                Provider only
                              </span>
                            )}
                            {saving && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
                          </div>
                          <p className="text-[10px] text-slate-500 leading-snug mt-0.5">
                            {muted ? `Not applicable for ${featureFlags.account_type} tenants. ` : ''}
                            {f.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Billing Audit */}
            <div className="px-5 py-3 border-b border-slate-800" data-testid="billing-audit-panel">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><CreditCard className="w-3 h-3" /> Billing Audit</h3>
                <button
                  onClick={() => fetchBillingAudit(detail.tenant_id)}
                  disabled={billingAuditLoading}
                  className="text-[11px] px-2.5 py-1 rounded-md border border-slate-700 bg-slate-800/60 text-slate-400 hover:text-slate-300 hover:border-slate-600 transition-all disabled:opacity-50 flex items-center gap-1"
                  data-testid="refresh-billing-audit-btn"
                >
                  {billingAuditLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
                </button>
              </div>
              {!billingAudit ? (
                <p className="text-[10px] text-slate-600">Loading…</p>
              ) : !billingAudit.has_subscription ? (
                <p className="text-[10px] text-slate-600">No subscription record.</p>
              ) : billingAudit.error ? (
                <p className="text-[10px] text-red-400">{billingAudit.error}</p>
              ) : (
                <div className="space-y-2 text-[11px]">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-md bg-slate-800/50 p-2">
                      <p className="text-slate-500 text-[10px] mb-0.5">Local addons $/mo</p>
                      <p className="text-slate-200 font-semibold">${(billingAudit.current_billing?.total_monthly ?? 0).toFixed(2)}</p>
                    </div>
                    <div className="rounded-md bg-slate-800/50 p-2">
                      <p className="text-slate-500 text-[10px] mb-0.5">Stripe $/mo</p>
                      <p className="text-slate-200 font-semibold">
                        {billingAudit.stripe?.available
                          ? `$${(billingAudit.stripe.total_monthly ?? 0).toFixed(2)}`
                          : <span className="text-slate-600">n/a</span>}
                      </p>
                    </div>
                    <div className={`rounded-md p-2 ${Math.abs(billingAudit.delta_monthly || 0) > 0.001 ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
                      <p className="text-slate-500 text-[10px] mb-0.5">Expected $/mo</p>
                      <p className={`font-semibold ${Math.abs(billingAudit.delta_monthly || 0) > 0.001 ? 'text-amber-300' : 'text-emerald-300'}`}>
                        ${(billingAudit.expected_billing?.total_monthly ?? 0).toFixed(2)}
                        {Math.abs(billingAudit.delta_monthly || 0) > 0.001 && (
                          <span className="ml-1 text-[10px]">({billingAudit.delta_monthly > 0 ? '+' : ''}${billingAudit.delta_monthly.toFixed(2)})</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Resource counts vs needed extras */}
                  <div className="rounded-md border border-slate-800 overflow-hidden">
                    <table className="w-full text-[10px]">
                      <thead className="bg-slate-800/40 text-slate-500 uppercase tracking-wider">
                        <tr><th className="text-left px-2 py-1">Resource</th><th className="text-center px-2 py-1">Actual</th><th className="text-center px-2 py-1">Local Add-on</th><th className="text-center px-2 py-1">Needed</th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {[
                          ['Companies', billingAudit.usage?.companies, billingAudit.current_addons?.extra_companies, billingAudit.needed_extras?.extra_companies],
                          ['Admins', billingAudit.usage?.admins, billingAudit.current_addons?.extra_admins, billingAudit.needed_extras?.extra_admins],
                          ['Users', billingAudit.usage?.users, billingAudit.current_addons?.extra_users, billingAudit.needed_extras?.extra_users],
                          ...(billingAudit.plan_type === 'provider' ? [['Orgs', billingAudit.usage?.orgs, billingAudit.current_addons?.extra_orgs, billingAudit.needed_extras?.extra_orgs]] : []),
                        ].map(([label, actual, local, needed]) => {
                          const mismatch = (local ?? 0) !== (needed ?? 0);
                          return (
                            <tr key={label} className={mismatch ? 'bg-amber-500/5' : ''}>
                              <td className="px-2 py-1 text-slate-300">{label}</td>
                              <td className="text-center text-slate-300">{actual ?? 0}</td>
                              <td className="text-center text-slate-400">{local ?? 0}</td>
                              <td className={`text-center font-medium ${mismatch ? 'text-amber-300' : 'text-emerald-400'}`}>{needed ?? 0}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Stripe live items */}
                  {billingAudit.stripe?.available && billingAudit.stripe.items?.length > 0 && (
                    <details className="rounded-md border border-slate-800" data-testid="stripe-items-details">
                      <summary className="px-2 py-1 text-[10px] text-slate-400 cursor-pointer hover:text-slate-200">Stripe line items ({billingAudit.stripe.items.length})</summary>
                      <div className="px-2 py-1 space-y-0.5">
                        {billingAudit.stripe.items.map((it) => (
                          <div key={it.id} className="flex justify-between text-[10px] text-slate-400">
                            <span>{it.product_name} × {it.quantity}</span>
                            <span>${it.line_total.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {/* Sync actions */}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => syncTenantBilling(detail.tenant_id, false)}
                      disabled={billingSyncing}
                      className="text-[11px] px-2.5 py-1 rounded-md bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:brightness-125 disabled:opacity-50 flex items-center gap-1"
                      data-testid="sync-billing-noprorate-btn"
                    >
                      {billingSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                      Sync (no proration)
                    </button>
                    <button
                      onClick={() => syncTenantBilling(detail.tenant_id, true)}
                      disabled={billingSyncing}
                      className="text-[11px] px-2.5 py-1 rounded-md bg-rose-500/10 text-rose-400 border border-rose-500/30 hover:brightness-125 disabled:opacity-50 flex items-center gap-1"
                      data-testid="sync-billing-prorate-btn"
                    >
                      Sync (prorate now)
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Orphan Claims Tool */}
            <div className="px-5 py-3 border-b border-slate-800">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Orphan Claims</h3>
                <button onClick={fetchOrphans} disabled={orphanLoading} data-testid="scan-orphans-btn"
                  className="text-[11px] px-2.5 py-1 rounded-md border border-slate-700 bg-slate-800/60 text-slate-400 hover:text-slate-300 hover:border-slate-600 transition-all disabled:opacity-50 flex items-center gap-1">
                  {orphanLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />} Scan
                </button>
                {orphanData && (
                  <button onClick={() => setOrphanData(null)} data-testid="close-orphans-btn"
                    className="text-[11px] px-2 py-1 rounded-md border border-slate-700 bg-slate-800/60 text-slate-400 hover:text-red-400 hover:border-red-600/50 transition-all flex items-center gap-1">
                    <X className="w-3 h-3" /> Close
                  </button>
                )}
              </div>
              <p className="text-[10px] text-slate-600 mb-2">Find claims with no tenant_id and assign them to the selected tenant.</p>
              {orphanData && (
                <div className="space-y-1.5" data-testid="orphan-results">
                  {orphanData.total === 0 ? (
                    <p className="text-[11px] text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> No orphan claims found</p>
                  ) : (
                    <>
                      <p className="text-[11px] text-amber-400">{orphanData.total} orphan claim{orphanData.total !== 1 ? 's' : ''} found:</p>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {orphanData.groups.map(g => (
                          <div key={g.company_name} className="flex items-center justify-between text-[10px] bg-slate-800/50 rounded px-2 py-1">
                            <span className="text-slate-300 truncate flex-1">{g.company_name} <span className="text-slate-500">({g.count})</span></span>
                            <button onClick={() => assignOrphans(g.company_name)} disabled={orphanAssigning}
                              data-testid={`assign-orphan-${g.company_name.replace(/\s+/g, '-').toLowerCase()}`}
                              className="text-[10px] px-2 py-0.5 rounded border border-emerald-600/40 bg-emerald-600/10 text-emerald-400 hover:bg-emerald-600/20 disabled:opacity-50 shrink-0 ml-2">
                              Assign
                            </button>
                          </div>
                        ))}
                      </div>
                      <button onClick={() => assignOrphans(null)} disabled={orphanAssigning}
                        data-testid="assign-all-orphans-btn"
                        className="text-[11px] px-3 py-1 rounded-md border border-amber-600/50 bg-amber-600/10 text-amber-300 hover:bg-amber-600/20 disabled:opacity-50 flex items-center gap-1 mt-1">
                        {orphanAssigning ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                        Assign All to {tenants.find(t => t.tenant_id === selected)?.name || 'selected tenant'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Data Migration */}
            <div className="px-5 py-3 border-b border-slate-800">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><Database className="w-3 h-3" /> Data Migration</h3>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => handleMigration(true, true)}
                  disabled={migrating}
                  data-testid="migrate-all-btn"
                  className="text-[11px] px-3 py-1.5 rounded-md border border-amber-600/50 bg-amber-600/10 text-amber-300 hover:bg-amber-600/20 transition-all disabled:opacity-50 flex items-center gap-1.5"
                >
                  {migrating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Database className="w-3 h-3" />}
                  {migrating ? 'Migrating...' : 'Migrate All'}
                </button>
                <button
                  onClick={() => handleMigration(true, false)}
                  disabled={migrating}
                  data-testid="migrate-pics-btn"
                  className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-700 bg-slate-800/60 text-slate-400 hover:text-slate-300 hover:border-slate-600 transition-all disabled:opacity-50"
                >
                  Pictures Only
                </button>
                <button
                  onClick={() => handleMigration(false, true)}
                  disabled={migrating}
                  data-testid="migrate-notes-btn"
                  className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-700 bg-slate-800/60 text-slate-400 hover:text-slate-300 hover:border-slate-600 transition-all disabled:opacity-50"
                >
                  Notes Only
                </button>
                <button
                  onClick={fetchMigrationStatus}
                  disabled={migrating || statusLoading}
                  data-testid="migration-status-btn"
                  className="text-[11px] px-2.5 py-1.5 rounded-md border border-sky-600/50 bg-sky-600/10 text-sky-400 hover:bg-sky-600/20 transition-all disabled:opacity-50 flex items-center gap-1"
                >
                  {statusLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <BarChart3 className="w-3 h-3" />}
                  Check Status
                </button>
                {migrating && (
                  <button
                    onClick={stopMigration}
                    data-testid="migrate-stop-btn"
                    className="text-[11px] px-2.5 py-1.5 rounded-md border border-red-600/50 bg-red-600/10 text-red-400 hover:bg-red-600/20 transition-all"
                  >
                    Stop
                  </button>
                )}
              </div>
              <p className="text-[10px] text-slate-600 mt-1.5">Downloads external images & decodes Base64 notes. Auto-processes in batches of 5.</p>
              <button onClick={fetchUploadsDiag} className="text-[10px] text-slate-500 hover:text-sky-400 mt-1 flex items-center gap-1" data-testid="uploads-diag-btn">
                <Search className="w-3 h-3" /> Disk Check
              </button>

              {/* Uploads diagnostic */}
              {uploadsDiag && (
                <div className="mt-2 p-2.5 rounded bg-slate-800/60 border border-slate-700 text-[10px] space-y-1" data-testid="uploads-diag-panel">
                  <div className="flex items-center justify-between">
                    <p className="text-slate-400 font-medium uppercase tracking-wider">Storage Diagnostic</p>
                    <button onClick={() => setUploadsDiag(null)} className="text-slate-600 hover:text-slate-400 text-[9px]">Close</button>
                  </div>
                  <p className="text-slate-300">Local Disk: <span className={uploadsDiag.local_disk?.file_count > 0 ? 'text-emerald-400' : 'text-red-400'}>{uploadsDiag.local_disk?.file_count || 0} files</span>
                    {uploadsDiag.local_disk?.total_size_mb > 0 && <span className="text-slate-500"> ({uploadsDiag.local_disk.total_size_mb} MB)</span>}
                  </p>
                  <p className="text-slate-300">GridFS (persistent): <span className={uploadsDiag.gridfs?.file_count > 0 ? 'text-emerald-400' : 'text-amber-400'}>{uploadsDiag.gridfs?.file_count || 0} files</span>
                    {uploadsDiag.gridfs?.total_size_mb > 0 && <span className="text-slate-500"> ({uploadsDiag.gridfs.total_size_mb} MB)</span>}
                  </p>
                  {uploadsDiag.local_disk?.file_count === 0 && uploadsDiag.gridfs?.file_count === 0 && (
                    <p className="text-red-400 font-medium">No files on disk or GridFS — uploads are empty!</p>
                  )}
                  {uploadsDiag.local_disk?.file_count > 0 && uploadsDiag.gridfs?.file_count < uploadsDiag.local_disk?.file_count && (
                    <button
                      onClick={migrateToGridFS}
                      disabled={gridfsMigrating}
                      data-testid="migrate-gridfs-btn"
                      className="mt-1 text-[10px] px-2.5 py-1 rounded border border-emerald-600/50 bg-emerald-600/10 text-emerald-300 hover:bg-emerald-600/20 flex items-center gap-1 disabled:opacity-50"
                    >
                      {gridfsMigrating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Database className="w-3 h-3" />}
                      {gridfsMigrating ? 'Migrating...' : `Migrate ${uploadsDiag.local_disk.file_count - (uploadsDiag.gridfs?.file_count || 0)} files to GridFS`}
                    </button>
                  )}
                  {uploadsDiag.gridfs?.file_count > 0 && uploadsDiag.local_disk?.file_count === 0 && (
                    <p className="text-emerald-400 text-[9px]">Files are safely stored in GridFS (MongoDB) and will persist across deployments.</p>
                  )}
                </div>
              )}

              {/* Live progress while migrating */}
              {migrating && migrationProgress && (
                <div className="mt-2 p-2.5 rounded bg-amber-500/5 border border-amber-500/20 text-[10px] space-y-1 animate-pulse" data-testid="migration-progress">
                  <p className="text-amber-300 font-medium">Batch {migrationProgress.batches} processing...</p>
                  {migrationProgress.pictures.total_checked > 0 && (
                    <p className="text-slate-300">
                      Pictures so far: <span className="text-emerald-400">{migrationProgress.pictures.files_downloaded} downloaded</span>, {migrationProgress.pictures.claims_updated} claims updated
                      {migrationProgress.pictures.json_strings_parsed > 0 && <span className="text-sky-400"> ({migrationProgress.pictures.json_strings_parsed} JSON parsed)</span>}
                      {migrationProgress.pictures.failed > 0 && <span className="text-red-400"> ({migrationProgress.pictures.failed} failed)</span>}
                      <span className="text-slate-500"> — {migrationProgress.pictures.total_checked} checked</span>
                    </p>
                  )}
                  {migrationProgress.notes.inserted > 0 && (
                    <p className="text-slate-300">
                      Notes so far: <span className="text-emerald-400">{migrationProgress.notes.inserted} inserted</span>
                    </p>
                  )}
                </div>
              )}

              {/* Final results */}
              {!migrating && migrationResult && (
                <div className="mt-2 p-2.5 rounded bg-slate-800/60 border border-slate-700 text-[10px] space-y-1" data-testid="migration-result">
                  <p className="text-slate-400 font-medium">
                    {migrationResult.stopped ? 'Stopped by user' : 'Complete'} — {migrationResult.batches} batch{migrationResult.batches !== 1 ? 'es' : ''} processed
                  </p>
                  {migrationResult.pictures && (
                    <>
                      <p className="text-slate-300">
                        Pictures: <span className="text-emerald-400">{migrationResult.pictures.files_downloaded} downloaded</span>, {migrationResult.pictures.claims_updated} claims updated
                        {migrationResult.pictures.json_strings_parsed > 0 && <span className="text-sky-400"> ({migrationResult.pictures.json_strings_parsed} JSON parsed)</span>}
                        {migrationResult.pictures.failed > 0 && <span className="text-red-400"> ({migrationResult.pictures.failed} failed)</span>}
                        <span className="text-slate-500"> — {migrationResult.pictures.total_checked} total checked</span>
                      </p>
                      {migrationResult.pictures.error_samples?.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          <p className="text-red-400 text-[9px] uppercase tracking-wider">Failure reasons:</p>
                          {migrationResult.pictures.error_samples.slice(0, 5).map((e, i) => (
                            <p key={`${e}-${i}`} className="text-[9px] text-red-300/70 font-mono break-all">{e}</p>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {migrationResult.notes && (
                    <p className="text-slate-300">
                      Notes: <span className="text-emerald-400">{migrationResult.notes.inserted} inserted</span>, {migrationResult.notes.duplicates_skipped} duplicates skipped
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Migration Status Panel */}
            {migrationStatus && !migrating && (
              <div className="px-5 py-3 border-b border-slate-800">
                <div className="p-2.5 rounded bg-slate-800/60 border border-slate-700 text-[10px] space-y-2" data-testid="migration-status-panel">
                  <div className="flex items-center justify-between">
                    <p className="text-slate-400 font-medium uppercase tracking-wider">Migration Status</p>
                    <button onClick={() => setMigrationStatus(null)} className="text-slate-600 hover:text-slate-400 text-[9px]">Close</button>
                  </div>
                  <div className="space-y-1">
                    <p className="text-slate-300">
                      Pictures: <span className="text-emerald-400">{migrationStatus.pictures.local} local</span>
                      {migrationStatus.pictures.pending > 0 && <span className="text-amber-400"> / {migrationStatus.pictures.pending} pending</span>}
                      {migrationStatus.pictures.failed > 0 && <span className="text-red-400"> / {migrationStatus.pictures.failed} failed</span>}
                      {migrationStatus.pictures.unparsed_json_claims > 0 && <span className="text-sky-400"> / {migrationStatus.pictures.unparsed_json_claims} unparsed JSON</span>}
                      <span className="text-slate-500"> — {migrationStatus.pictures.total_files} total files</span>
                    </p>
                    <p className="text-slate-300">Notes: <span className="text-emerald-400">{migrationStatus.notes.migrated_count} migrated</span></p>
                    {migrationStatus.pictures.fully_migrated ? (
                      <p className="text-emerald-400 font-medium flex items-center gap-1"><Check className="w-3 h-3" /> All pictures fully migrated</p>
                    ) : (
                      <p className="text-amber-400 text-[9px]">
                        {migrationStatus.pictures.pending > 0 && `${migrationStatus.pictures.pending} picture(s) still need downloading. `}
                        {migrationStatus.pictures.failed > 0 && `${migrationStatus.pictures.failed} picture(s) failed (expired URLs etc).`}
                      </p>
                    )}
                  </div>
                  {migrationStatus.pictures.failed > 0 && (
                    <button
                      onClick={retryFailedMigration}
                      data-testid="retry-failed-btn"
                      className="text-[10px] px-2.5 py-1 rounded border border-amber-600/50 bg-amber-600/10 text-amber-300 hover:bg-amber-600/20 flex items-center gap-1"
                    >
                      <RefreshCw className="w-3 h-3" /> Clear Failed Flags & Retry
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-xs text-slate-500 uppercase tracking-wider">Members</h3>
              <div className="flex items-center gap-2">
                <button onClick={handleOpenAddExisting} className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1" data-testid="add-existing-user-btn">
                  <UserPlus className="w-3 h-3" /> Add Existing
                </button>
                <button onClick={() => { setShowAddMember(!showAddMember); setShowAddExisting(false); }} className="text-emerald-400 hover:text-emerald-300 text-xs flex items-center gap-1" data-testid="add-member-to-tenant-btn">
                  <Plus className="w-3 h-3" /> New
                </button>
              </div>
            </div>

            {/* Add Existing User Form */}
            {showAddExisting && (
              <div className="px-5 py-3 border-b border-slate-800 bg-slate-800/40" data-testid="add-existing-user-form">
                <div className="space-y-2">
                  <Input
                    value={existingSearch}
                    onChange={(e) => searchAvailableUsers(e.target.value)}
                    placeholder="Search by name or email..."
                    className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs"
                    data-testid="existing-user-search"
                  />
                  <div className="max-h-36 overflow-y-auto rounded border border-slate-700 bg-slate-900">
                    {loadingAvailable ? (
                      <p className="px-3 py-2 text-[10px] text-slate-500">Searching...</p>
                    ) : availableUsers.length === 0 ? (
                      <p className="px-3 py-2 text-[10px] text-slate-500">No users found</p>
                    ) : (
                      availableUsers.map((u) => (
                        <div
                          key={u.user_id}
                          onClick={() => setSelectedExisting(u)}
                          className={`px-3 py-2 flex items-center justify-between cursor-pointer transition-colors ${selectedExisting?.user_id === u.user_id ? 'bg-blue-600/20 border-l-2 border-blue-400' : 'hover:bg-slate-800'}`}
                          data-testid={`available-user-${u.user_id}`}
                        >
                          <div>
                            <p className="text-xs text-slate-200">{u.name}</p>
                            <p className="text-[10px] text-slate-500">{u.email}</p>
                          </div>
                          <span className="text-[10px] text-slate-500">{u.tenant_id || 'No org'}</span>
                        </div>
                      ))
                    )}
                  </div>
                  {selectedExisting && (
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">Assign as:</span>
                      <Select value={existingRole} onValueChange={setExistingRole}>
                        <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-300 h-7 text-xs flex-1" data-testid="existing-user-role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">Viewer</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="super_admin">Super Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={handleAddExistingMember} disabled={addingExisting} className="bg-blue-600 hover:bg-blue-700 h-7 text-xs px-3" data-testid="add-existing-submit">
                        {addingExisting ? 'Adding...' : 'Add'}
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setShowAddExisting(false)} className="text-slate-400 h-7 text-xs px-3">Cancel</Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Add Member Form */}
            {showAddMember && (
              <div className="px-5 py-3 border-b border-slate-800 bg-slate-800/40" data-testid="add-member-form">
                <form onSubmit={handleAddMember} className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: toProperCase(e.target.value) })} placeholder="Full Name *" className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" required data-testid="new-member-name" />
                    <Input type="email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} placeholder="Email *" className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" required data-testid="new-member-email" />
                    <Input type="password" value={newMember.password} onChange={(e) => setNewMember({ ...newMember, password: e.target.value })} placeholder="Password *" className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" required data-testid="new-member-password" />
                    <Select value={newMember.role} onValueChange={(val) => setNewMember({ ...newMember, role: val })}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-300 h-8 text-xs" data-testid="new-member-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">Viewer</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={addingMember} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs px-3" data-testid="create-member-submit">{addingMember ? 'Adding...' : 'Add Member'}</Button>
                    <Button type="button" variant="ghost" onClick={() => setShowAddMember(false)} className="text-slate-400 h-7 text-xs px-3">Cancel</Button>
                  </div>
                </form>
              </div>
            )}

            <div className="divide-y divide-slate-800">
              {(detail.users || []).map((u) => (
                <div key={u.user_id} className="px-5 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    {u.picture ? <img src={u.picture} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-[10px] font-medium text-slate-300">{u.name?.[0]?.toUpperCase() || '?'}</div>}
                    <div><p className="text-xs text-slate-200">{u.name}</p><p className="text-[10px] text-slate-500">{u.email}</p></div>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${ROLE_COLORS[u.role] || 'text-slate-400 bg-slate-700'}`}>{u.role?.replace('_', ' ')}</span>
                </div>
              ))}
              {(!detail.users || detail.users.length === 0) && !showAddMember && <p className="px-5 py-3 text-xs text-slate-500">No members</p>}
            </div>

            {/* Companies */}
            <div className="px-5 py-3 border-b border-t border-slate-800 flex items-center justify-between">
              <h3 className="text-xs text-slate-500 uppercase tracking-wider">Companies</h3>
              <button onClick={() => setShowAddCompany(!showAddCompany)} className="text-emerald-400 hover:text-emerald-300 text-xs flex items-center gap-1" data-testid="add-company-to-tenant-btn">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>

            {/* Add Company Form */}
            {showAddCompany && (
              <div className="px-5 py-3 border-b border-slate-800 bg-slate-800/40" data-testid="add-company-form">
                <form onSubmit={handleAddCompany} className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={newCompany.name} onChange={(e) => setNewCompany({ ...newCompany, name: toProperCase(e.target.value) })} placeholder="Company Name *" className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" required data-testid="new-company-name" />
                    <Input value={newCompany.dot_number} onChange={(e) => setNewCompany({ ...newCompany, dot_number: e.target.value })} placeholder="DOT#" className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" data-testid="new-company-dot" />
                    <Input value={newCompany.contact} onChange={(e) => setNewCompany({ ...newCompany, contact: toProperCase(e.target.value) })} placeholder="Contact" className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" />
                    <Input value={newCompany.email} onChange={(e) => setNewCompany({ ...newCompany, email: e.target.value })} placeholder="Email" className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" />
                    <Input value={newCompany.phone} onChange={(e) => setNewCompany({ ...newCompany, phone: e.target.value })} placeholder="Phone" className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={addingCompany} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs px-3" data-testid="create-company-submit">{addingCompany ? 'Creating...' : 'Create Company'}</Button>
                    <Button type="button" variant="ghost" onClick={() => setShowAddCompany(false)} className="text-slate-400 h-7 text-xs px-3">Cancel</Button>
                  </div>
                </form>
              </div>
            )}

            <div className="divide-y divide-slate-800">
              {(detail.companies || []).map((c, idx) => (
                <div key={c.id || c.name || idx} className="px-5 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-200">{c.name}</p>
                    <p className="text-[10px] text-slate-500">{[c.dot_number, c.contact, c.email].filter(Boolean).join(' · ')}</p>
                  </div>
                </div>
              ))}
              {(!detail.companies || detail.companies.length === 0) && !showAddCompany && <p className="px-5 py-3 text-xs text-slate-500">No companies</p>}
            </div>
          </div>
        )}
      </div>

      {/* Delete Tenant Confirmation Modal */}
      {confirmDeleteTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setConfirmDeleteTenant(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="delete-tenant-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-full flex items-center justify-center bg-red-500/15">
                <Trash2 className="w-4 h-4 text-red-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Delete Tenant</h3>
                <p className="text-xs text-slate-400">{confirmDeleteTenant.name}</p>
              </div>
            </div>
            <p className="text-xs text-slate-400 mb-1">This will permanently delete:</p>
            <ul className="text-xs text-slate-500 space-y-0.5 mb-4 pl-4 list-disc">
              <li>The organization and all settings</li>
              <li>{confirmDeleteTenant.user_count || 0} user(s)</li>
              <li>{confirmDeleteTenant.claim_count || 0} claim(s) and associated files</li>
              <li>{confirmDeleteTenant.company_count || 0} company/companies</li>
            </ul>
            <p className="text-[11px] text-red-400/80 mb-4">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" className="h-8 text-xs bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800" onClick={() => setConfirmDeleteTenant(null)} data-testid="delete-tenant-cancel">Cancel</Button>
              <Button className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={handleDeleteTenant} data-testid="delete-tenant-confirm">Delete Tenant</Button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Change Confirmation Modal */}
      {planConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setPlanConfirm(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="plan-confirm-modal">
            {/* Header */}
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center ${planConfirm.diff.isUpgrade ? 'bg-emerald-500/15' : 'bg-amber-500/15'}`}>
                {planConfirm.diff.isUpgrade
                  ? <ArrowRight className="w-4 h-4 text-emerald-400" />
                  : <AlertTriangle className="w-4 h-4 text-amber-400" />}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">{planConfirm.diff.isUpgrade ? 'Upgrade' : 'Switch'} Billing Plan</h3>
                <p className="text-xs text-slate-400">{planConfirm.tenantName}</p>
              </div>
            </div>

            {/* Subscription Status */}
            {planConfirm.diff.subStatus !== 'none' && (
              <div className="flex items-center gap-2 mb-4">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                  planConfirm.diff.subStatus === 'active' ? 'text-emerald-400 bg-emerald-400/10' :
                  planConfirm.diff.subStatus === 'trialing' ? 'text-blue-400 bg-blue-400/10' :
                  planConfirm.diff.subStatus === 'trial_expired' ? 'text-red-400 bg-red-400/10' :
                  'text-slate-400 bg-slate-700'
                }`}>Subscription: {planConfirm.diff.subStatus.replace('_', ' ')}</span>
                {planConfirm.diff.hasStripe && <span className="text-[10px] text-slate-600 font-mono">Stripe active</span>}
              </div>
            )}

            {/* From → To */}
            <div className="flex items-center gap-3 mb-4">
              {getPlanBadge(detail?.billing_plan)}
              <ArrowRight className="w-3.5 h-3.5 text-slate-600" />
              {getPlanBadge(planConfirm.plan)}
            </div>

            {/* Billing change */}
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg px-4 py-3 mb-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Billing</p>
              {planConfirm.costPreview ? (
                <div className="space-y-1 text-sm">
                  {planConfirm.costPreview.items.map((item, i) => (
                    <div key={item.label || i} className="flex justify-between">
                      <span className="text-slate-400">{item.label}{item.qty > 1 ? ` × ${item.qty}` : ''}</span>
                      <span className="text-slate-300">${item.total.toFixed(2)}/mo</span>
                    </div>
                  ))}
                  <div className="border-t border-slate-700 pt-1 flex justify-between">
                    <span className="text-slate-300 font-medium">Total</span>
                    <span className="text-white font-semibold">${planConfirm.costPreview.total_monthly.toFixed(2)}/mo</span>
                  </div>
                  {planConfirm.costPreview.total_monthly > planConfirm.costPreview.base && (
                    <p className="text-[10px] text-slate-500 mt-1">
                      Includes {planConfirm.costPreview.counts.companies} companies, {planConfirm.costPreview.counts.admins} admins, {planConfirm.costPreview.counts.users} users
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">Current</span><span className="text-slate-300">${planConfirm.diff.from.monthly}/mo</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">New</span><span className="text-white font-medium">${planConfirm.diff.to.monthly}/mo</span></div>
                </div>
              )}
            </div>

            {/* Limit Changes */}
            <div className="space-y-2 mb-5 max-h-52 overflow-y-auto">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Limit Changes</p>
              {planConfirm.diff.changes.map((item, i) => (
                <div key={item.text || i} className="flex items-center gap-2 py-0.5">
                  {item.type === 'added' ? <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> :
                   item.type === 'removed' ? <X className="w-3.5 h-3.5 text-red-500 shrink-0" /> :
                   <span className="w-3.5 h-3.5 text-slate-600 shrink-0 text-center">•</span>}
                  <span className={`text-xs ${item.type === 'unchanged' ? 'text-slate-500' : item.type === 'added' ? 'text-slate-300' : 'text-slate-400'}`}>{item.text}</span>
                </div>
              ))}

              {planConfirm.diff.warnings.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mt-2">
                  {planConfirm.diff.warnings.map((w, i) => (
                    <p key={`${w}-${i}`} className="text-xs text-amber-400 flex gap-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{w}</p>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" className="h-8 text-xs bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800" onClick={() => setPlanConfirm(null)} data-testid="plan-cancel-btn">Cancel</Button>
              <Button
                className={`h-8 text-xs text-white ${planConfirm.diff.isUpgrade ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                onClick={confirmPlanChange}
                data-testid="plan-confirm-btn"
              >
                {planConfirm.diff.hasStripe && planConfirm.diff.subStatus === 'active' ? 'Confirm & Update Billing' :
                 planConfirm.diff.isUpgrade ? 'Confirm Upgrade' : 'Confirm Switch'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DevTenants;